import {
	paymentMethodSharingToggleHelp,
	paymentMethodSharingToggleLabel,
} from '@supply-mover-2-0/core';
import React, { useId } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Toggle } from '../forms/Toggle';

interface PaymentMethodSharingToggleProps {
	checked: boolean;
	onChange: (checked: boolean) => void;
	/**
	 * The account the method is saved on, named in the label. Pass it only
	 * where that is not the account the user is looking at (a method added
	 * from an invoice's Pay panel can be saved to the invoice's account).
	 */
	accountName?: string | null;
	disabled?: boolean;
	className?: string;
}

/**
 * PD-17009 — the "share with every account in this ownership group" switch on
 * a saved payment method.
 *
 * One component for the CRM add and edit panel and the storefront add form
 * and wallet, so the label and the help text are the same wherever the switch
 * appears. The wording itself lives in libs/core (`payment-method-sharing.ts`).
 *
 * The caller decides WHETHER to render it: only when the account the method
 * belongs to is in an ownership group. This component assumes it is.
 *
 * Turning sharing on asks nothing, for a card or a bank account alike.
 * Turning it off is not confirmed here either. On a method being created
 * there is nothing to lose; on an existing method the caller shows what
 * depends on it (`PaymentMethodStopSharingDialog`) before saving the change.
 */
export function PaymentMethodSharingToggle({
	checked,
	onChange,
	accountName,
	disabled = false,
	className,
}: PaymentMethodSharingToggleProps): React.ReactElement {
	const { t } = useTranslation();
	const labelId = useId();
	const helpId = useId();

	return (
		<div className={cn('py-2', className)}>
			<div className="flex items-center gap-3">
				<Toggle
					checked={checked}
					onChange={onChange}
					disabled={disabled}
					ariaLabelledBy={labelId}
					ariaDescribedBy={helpId}
				/>
				<span id={labelId} className="text-sm font-medium text-default">
					{paymentMethodSharingToggleLabel(t, accountName)}
				</span>
			</div>
			{/* Help text sits on its own line below the control, in a block
			    element: an inline sibling would collapse onto the label's line. */}
			<p id={helpId} className="mt-1 text-xs text-muted">
				{paymentMethodSharingToggleHelp(t)}
			</p>
		</div>
	);
}
